/* eslint-disable parog-web/no-glint-expect-error-directive */
import { service } from '@ember/service';
import { type Signature as ProviderSig } from 'parog-web/components/forc/provider';
import BaseMedicalHistoryFormComponent from 'parog-web/components/medical-history/abstract/base-edition-form';
import { type SemeiaMedicSession } from 'parog-web/services/session';
import MedicalHistoryBaseEditionForm from './base-edition-form';

import OncoBreastCancerTnm0Pathology from 'parog-web/components/medical-history/onco/breast-cancer-tnm0/pathology-fields';
import OncoBreastCancerPathology from './onco/breast-cancer/pathology-fields';
import OncoLungCancerPathology from './onco/lung-cancer/pathology-fields';
import OncoPancreaticCancerPathology from './onco/pancreatic-cancer/pathology-fields';
import OncoProstateCancerPathology from './onco/prostate-cancer/pathology-fields';

import AccordionBlock from './accordion-block';
import OncoBrainCancerAntecedents from './onco/brain-cancer/antecedents-fields';
import OncoBreastCancerTnm0Antecedents from './onco/breast-cancer-tnm0/antecedents-fields';
import OncoBreastCancerAntecedents from './onco/breast-cancer/antecedents-fields';
import OncoCervixCancerAntecedents from './onco/cervix-cancer/antecedents-fields';
import OncoColonCancerAntecedents from './onco/colon-cancer/antecedents-fields';
import OncoHematoCancerAntecedents from './onco/hemato-cancer/antecedents-fields';
import OncoKidneyCancerAntecedents from './onco/kidney-cancer/antecedents-fields';
import OncoLiverCancerAntecedents from './onco/liver-cancer/antecedents-fields';
import OncoLungCancerAntecedents from './onco/lung-cancer/antecedents-fields';
import OncoLymphomaCancerAntecedents from './onco/lymphoma-cancer/antecedents-fields';
import OncoMyelomaCancerAntecedents from './onco/myeloma-cancer/antecedents-fields';
import OncoGenericCancerAntecedents from './onco/onco-generic/antecedents-fields';
import OncoOvarianCancerAntecedents from './onco/ovarian-cancer/antecedents-fields';
import OncoPancreaticCancerAntecedents from './onco/pancreatic-cancer/antecedents-fields';
import OncoProstateCancerAntecedents from './onco/prostate-cancer/antecedents-fields';
import OncoSkinCancerAntecedents from './onco/skin-cancer/antecedents-fields';
import OncoStomachCancerAntecedents from './onco/stomach-cancer/antecedents-fields';
import OncoTesticularCancerAntecedents from './onco/testicular-cancer/antecedents-fields';

import type { TemplateOnlyComponent } from '@ember/component/template-only';
import type { ComponentLike } from '@glint/template';
import type Patient from 'parog-web/models/patient';
import OncoBrainCancerDiagnostic from './onco/brain-cancer/diagnostic-fields';
import OncoBreastCancerTnm0Diagnostic from './onco/breast-cancer-tnm0/diagnostic-fields';
import OncoBreastCancerDiagnostic from './onco/breast-cancer/diagnostic-fields';
import OncoCervixCancerDiagnostic from './onco/cervix-cancer/diagnostic-fields';
import OncoColonCancerDiagnostic from './onco/colon-cancer/diagnostic-fields';
import OncoHematoCancerDiagnostic from './onco/hemato-cancer/diagnostic-fields';
import OncoKidneyCancerDiagnostic from './onco/kidney-cancer/diagnostic-fields';
import OncoLiverCancerDiagnostic from './onco/liver-cancer/diagnostic-fields';
import OncoLungCancerDiagnostic from './onco/lung-cancer/diagnostic-fields';
import OncoLymphomaCancerDiagnostic from './onco/lymphoma-cancer/diagnostic-fields';
import OncoMyelomaCancerDiagnostic from './onco/myeloma-cancer/diagnostic-fields';
import OncoGenericCancerDiagnostic from './onco/onco-generic/diagnostic-fields';
import OncoOvarianCancerDiagnostic from './onco/ovarian-cancer/diagnostic-fields';
import OncoPancreaticCancerDiagnostic from './onco/pancreatic-cancer/diagnostic-fields';
import OncoProstateCancerDiagnostic from './onco/prostate-cancer/diagnostic-fields';
import OncoSkinCancerDiagnostic from './onco/skin-cancer/diagnostic-fields';
import OncoStomachCancerDiagnostic from './onco/stomach-cancer/diagnostic-fields';
import OncoTesticularCancerDiagnostic from './onco/testicular-cancer/diagnostic-fields';

const PathologyFields = {
  'breast-cancer-tnm0': OncoBreastCancerTnm0Pathology,
  'breast-cancer': OncoBreastCancerPathology,
  'lung-cancer': OncoLungCancerPathology,
  'pancreatic-cancer': OncoPancreaticCancerPathology,
  'prostate-cancer': OncoProstateCancerPathology
};

const HistoryFields = {
  'pancreatic-cancer': OncoPancreaticCancerAntecedents,
  'colon-cancer': OncoColonCancerAntecedents,
  'kidney-cancer': OncoKidneyCancerAntecedents,
  'skin-cancer': OncoSkinCancerAntecedents,
  'ovarian-cancer': OncoOvarianCancerAntecedents,
  'breast-cancer-tnm0': OncoBreastCancerTnm0Antecedents,
  'stomach-cancer': OncoStomachCancerAntecedents,
  'breast-cancer': OncoBreastCancerAntecedents,
  'testicular-cancer': OncoTesticularCancerAntecedents,
  'lung-cancer': OncoLungCancerAntecedents,
  'prostate-cancer': OncoProstateCancerAntecedents,
  'myeloma-cancer': OncoMyelomaCancerAntecedents,
  'hemato-cancer': OncoHematoCancerAntecedents,
  'brain-cancer': OncoBrainCancerAntecedents,
  'liver-cancer': OncoLiverCancerAntecedents,
  'lymphoma-cancer': OncoLymphomaCancerAntecedents,
  'cervix-cancer': OncoCervixCancerAntecedents,
  'onco-generic': OncoGenericCancerAntecedents
};

const DiagnosticFields = {
  'pancreatic-cancer': OncoPancreaticCancerDiagnostic,
  'colon-cancer': OncoColonCancerDiagnostic,
  'kidney-cancer': OncoKidneyCancerDiagnostic,
  'skin-cancer': OncoSkinCancerDiagnostic,
  'ovarian-cancer': OncoOvarianCancerDiagnostic,
  'breast-cancer-tnm0': OncoBreastCancerTnm0Diagnostic,
  'stomach-cancer': OncoStomachCancerDiagnostic,
  'breast-cancer': OncoBreastCancerDiagnostic,
  'testicular-cancer': OncoTesticularCancerDiagnostic,
  'lung-cancer': OncoLungCancerDiagnostic,
  'prostate-cancer': OncoProstateCancerDiagnostic,
  'myeloma-cancer': OncoMyelomaCancerDiagnostic,
  'hemato-cancer': OncoHematoCancerDiagnostic,
  'brain-cancer': OncoBrainCancerDiagnostic,
  'liver-cancer': OncoLiverCancerDiagnostic,
  'lymphoma-cancer': OncoLymphomaCancerDiagnostic,
  'cervix-cancer': OncoCervixCancerDiagnostic,
  'onco-generic': OncoGenericCancerDiagnostic
};

export default class MedicalHistoryEditionFormComponent extends BaseMedicalHistoryFormComponent {
  @service declare session: SemeiaMedicSession;

  override journalEventProperties = [
    ...this.sharedProperties,
    'diagnosisDate',
    'side',
    'stage',
    'tnmClassification',
    'tnmComment',
    'histology',
    'her2',
    'fertilityPreservation',
    'oncogeneticConsultation',
    'estrogenReceptor',
    'progesteroneReceptor',
    'pelvicSonogram',
    'ki67',
    'donorType',
    'hemato-cancer-type',
    'maritalStatus',
    'hasChildren',
    'children',
    'job',
    'relapse',
    'tripleNegative',
    'familyHistoryBreastCancer',
    'familyHistoryBreastCancerMember',
    'familyHistoryOvarianCancer',
    'familyHistoryOvarianCancerMember',
    'brca1',
    'brca1Member',
    'brca2',
    'brca2Member',
    'otherCancer',
    'otherCancerType',
    'menopause',
    'contraception',
    'cellCancerType',
    'nonSmallCellCancerType',
    'mutation',
    'statutPDL1',
    'smokingPatients',
    'lungCancerFamily',
    'chronicBronchopneumopathy',
    'pancreaticCancerType',
    'pancreaticMutation',
    'familialPancreaticCancer',
    'hereditaryChronicPrss1GeneMutation',
    'familyHistoryProstateCancer',
    'familyHistoryProstateCancerMember',
    'prostateCancerType',
    'hormoneSensitiveCancer',
    'prostateMutation',
    'gleasonScore',
    'smokingCessation'
  ];

  get diagnostic(): TemplateOnlyComponent {
    return DiagnosticFields[this.patho ?? ''];
  }

  get History(): ComponentLike<{
    Args: {
      form: ProviderSig['Blocks']['content'][0];
      patient: Patient;
    };
  }> {
    return HistoryFields[this.patho ?? ''];
  }

  get pathology(): TemplateOnlyComponent {
    return PathologyFields[this.patho ?? ''];
  }

  override retrieveModel(): Record<string, unknown> {
    const model = super.retrieveModel() as {
      tnmClassification;
      estrogenReceptor;
      progesteroneReceptor;
      children;
    };

    model.tnmClassification ??= {};
    model.estrogenReceptor ??= {};
    model.progesteroneReceptor ??= {};
    model.children ??= [];

    if (this.args.patient.hasBreastCancer)
      model.tnmClassification.m =
        `${this.args.patient.followUpReasonRecord?.id}`.endsWith('tnm0')
          ? 'M0'
          : 'M1';

    return model;
  }

  <template>
    <MedicalHistoryBaseEditionForm
      {{! @glint-expect-error }}
      @forcComponent={{this}}
      @patient={{@patient}}
      @changeset={{this.changeset}}
      as |mh|
    >
      <mh.FurSection />
      <mh.FurDetailsSection @diagnostic={{this.diagnostic}} />
      <mh.ComorbiditiesAllergiesSection />
      <mh.PathologySection @pathology={{this.pathology}} />
      {{#if this.History}}
        <AccordionBlock
          @title='Antécédents'
          @isActive={{false}}
          class='pt-[1em]'
        >

          <this.History @form={{mh.form}} @patient={{@patient}} />
        </AccordionBlock>
      {{/if}}

      <mh.VaccinesSection />
      <mh.CommentsSection />
    </MedicalHistoryBaseEditionForm>
  </template>
}
